import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Select, Input, Spin } from "antd";
import { NotificationManager } from "react-notifications";
import PurchaseOrderService from "../../services/purchaseOrder.service";
import CompanyLocationService from "../../services/company-location.service";
import RouteCard from "../DiamondTrade/RouteCard";

const { Option } = Select;

// DataSet->JSON serialization renders any NULL column as {} instead of null (established pattern,
// see Addresses.js) -- applies to address fields returned here too.
const safe = (v) => (v && typeof v === "object" ? "" : v ?? "");
const isTrue = (v) => v === true || v === 1 || v === "1";

// Matches Diamond-PO-Prototype-v3.html's rBasket() exactly: mode toggle, stone list, order
// summary, addresses (RouteCard), terms, Send -- now wired to F2's CreateFromBasket (Task 2172).

export const Basket = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const loginDetials = useSelector((state) => state.loginReducer);
  const dealerId = loginDetials?.loginDetials?.responseData?.dealerId;
  const dealerCompany = loginDetials?.loginDetials?.responseData?.dealerCompany;

  const [mode, setMode] = useState(location.state?.mode || "MEMO");
  const [stones, setStones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [receivingAddresses, setReceivingAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [supplierShippingAddress, setSupplierShippingAddress] = useState(null);
  const [memoDays, setMemoDays] = useState("14");
  const [terms, setTerms] = useState("NET30");
  const [reference, setReference] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!dealerId) return;
    loadBasket();
    loadReceivingAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealerId]);

  const loadBasket = () => {
    setLoading(true);
    PurchaseOrderService.GetMyDiamondHolds({ DealerID: String(dealerId) })
      .then((response) => {
        setLoading(false);
        const rows = response?.data?.responseData?.Table || [];
        setStones(rows);
        if (rows.length > 0) loadSupplierShippingAddress(rows[0].SupplierDealerID);
      })
      .catch(() => setLoading(false));
  };

  const loadReceivingAddresses = () => {
    CompanyLocationService.GetPOLocationDetails({ DealerID: dealerId }).then((response) => {
      if (response.data.message === "Success" && response.data.responseData?.Table) {
        const rows = response.data.responseData.Table.filter(
          (r) => r.LocationType === "LocationId2" && isTrue(r.IsActive) && r.AddressKind === "RECEIVING"
        );
        setReceivingAddresses(rows);
        const def = rows.find((r) => isTrue(r.IsDefault)) || rows[0];
        if (def) setSelectedAddressId(def.LocationId);
      }
    });
  };

  const loadSupplierShippingAddress = (supplierDealerId) => {
    CompanyLocationService.GetPOLocationDetails({ DealerID: supplierDealerId }).then((response) => {
      if (response.data.message === "Success" && response.data.responseData?.Table) {
        const rows = response.data.responseData.Table.filter(
          (r) => r.LocationType === "LocationId2" && isTrue(r.IsActive) && r.AddressKind === "SHIPPING"
        );
        const def = rows.find((r) => isTrue(r.IsDefault)) || rows[0];
        setSupplierShippingAddress(def || null);
      }
    });
  };

  const handleRemoveStone = (dInventoryId) => {
    PurchaseOrderService.ReleaseDiamondHold({ DInventoryID: String(dInventoryId), DealerID: String(dealerId) }).then((response) => {
      if (response.data.status === true) {
        NotificationManager.success(t("Removed from your basket"));
        loadBasket();
        window.dispatchEvent(new CustomEvent("basketChanged"));
      } else {
        NotificationManager.error(t(response.data.message || "Could not remove"));
      }
    });
  };

  const handleSend = () => {
    setSending(true);
    PurchaseOrderService.CreateFromBasket({
      DealerID: String(dealerId),
      Kind: isMemo ? "MEMO" : "PO",
      DInventoryIDs: stones.map((s) => s.DInventoryID).join(","),
      ReceivingAddressId: selectedAddressId != null ? String(selectedAddressId) : null,
      MemoDays: isMemo ? Number(memoDays) : null,
      Terms: !isMemo ? terms : null,
      ReferenceNo: reference,
    })
      .then((response) => {
        setSending(false);
        if (response.data.status === true) {
          const docNumber = response.data.responseData?.DocNumber;
          NotificationManager.success(isMemo ? t("Memo request sent — {{docNumber}}", { docNumber }) : t("Purchase order placed — {{docNumber}}", { docNumber }));
          setReference("");
          loadBasket();
          window.dispatchEvent(new CustomEvent("basketChanged"));
        } else {
          NotificationManager.error(t(response.data.message || "Could not send"));
        }
      })
      .catch(() => {
        setSending(false);
        NotificationManager.error(t("Could not send"));
      });
  };

  const selectedAddress = receivingAddresses.find((a) => a.LocationId === selectedAddressId);
  const blocked = !selectedAddress || !supplierShippingAddress;
  const total = stones.reduce((sum, s) => sum + Number(s.Price || 0), 0);
  const supplierName = stones[0]?.SupplierCompany;
  const isMemo = mode === "MEMO";

  const modeToggle = (
    <div style={{ display: "inline-flex", border: "1px solid #d3d9e5", borderRadius: 6, overflow: "hidden" }}>
      <button
        onClick={() => setMode("MEMO")}
        style={{
          padding: "7px 13px",
          fontSize: 12.5,
          border: "none",
          cursor: "pointer",
          background: isMemo ? "#3b5bdb" : "#fff",
          color: isMemo ? "#fff" : "#6b7689",
        }}
      >
        {t("Request on memo")}
      </button>
      <button
        onClick={() => setMode("PURCHASE")}
        style={{
          padding: "7px 13px",
          fontSize: 12.5,
          border: "none",
          borderLeft: "1px solid #d3d9e5",
          cursor: "pointer",
          background: !isMemo ? "#3b5bdb" : "#fff",
          color: !isMemo ? "#fff" : "#6b7689",
        }}
      >
        {t("Buy now · purchase order")}
      </button>
    </div>
  );

  return (
    <div className="basket-page" style={{ padding: 20, paddingBottom: 100 }}>
      <Spin spinning={loading}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
          <div>
            <h2 style={{ margin: 0 }}>{t("My Basket")}</h2>
            <p style={{ color: "#888", margin: 0 }}>{t("Stones you are holding")}</p>
          </div>
          {stones.length > 0 && modeToggle}
        </div>

        {stones.length === 0 ? (
          <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 8, padding: 40, textAlign: "center" }}>
            <div style={{ fontSize: 28, marginBottom: 10 }}>◇</div>
            <div style={{ marginBottom: 14 }}>
              {t("Nothing held yet. Press")} <b>{t("Hold & add to basket")}</b> {t("on any listed stone. You then decide whether to take it on memo or buy it outright.")}
            </div>
            <Button type="primary" onClick={() => navigate("/diamondSearch")}>
              {t("Find diamonds")}
            </Button>
          </div>
        ) : (
          <>
            <div
              style={{
                background: isMemo ? "#e6f4ff" : "#e9f9f0",
                border: `1px solid ${isMemo ? "#91caff" : "#b7eacb"}`,
                borderRadius: 6,
                padding: 14,
                marginBottom: 16,
              }}
            >
              <b>{isMemo ? t("Memo request — {{supplier}} keeps title (consignment)", { supplier: supplierName }) : t("Direct purchase — title passes to you on delivery")}</b>
              <div style={{ marginTop: 6 }}>
                {isMemo
                  ? t("The supplier ships the stones for you to show a customer. They may ship them together or one at a time, and may add a memo margin when they ship. Anything you don't buy goes back with your return shipping details.")
                  : t("No memo, no due date, no margin. The supplier confirms and ships; you inspect, accept, and the purchase order becomes the invoice.")}
              </div>
            </div>

            {blocked && (
              <div style={{ background: "#fff2f0", border: "1px solid #ffccc7", borderRadius: 6, padding: 14, marginBottom: 16 }}>
                <b>{!selectedAddress ? t("You have no receiving address") : t("{{supplier}} has no shipping address", { supplier: supplierName })}</b>
                <div style={{ marginTop: 6 }}>
                  {!selectedAddress
                    ? t("Every memo and purchase order is addressed to one of your receiving addresses and freezes a copy of it. Add one to continue.")
                    : t("The supplier has to set a ship-from address before an order can be raised against them.")}
                </div>
                {!selectedAddress && (
                  <div style={{ marginTop: 8 }}>
                    <Button size="small" onClick={() => navigate("/addresses")}>
                      {t("Add a receiving address")}
                    </Button>
                  </div>
                )}
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: 16, alignItems: "start" }}>
              <div>
                {stones.map((s) => (
                  <div
                    key={s.DInventoryID}
                    style={{
                      display: "flex",
                      gap: 14,
                      alignItems: "center",
                      border: "1px solid #eee",
                      borderRadius: 9,
                      padding: "12px 14px",
                      background: "#fff",
                      marginBottom: 9,
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600 }}>
                        {safe(s.Carat)}ct {safe(s.Shape)} {safe(s.Color)} {safe(s.Clarity)}
                      </div>
                      <div style={{ fontSize: 12, color: "#6b7689", fontFamily: "monospace" }}>
                        {t("Stock")} {safe(s.StockNo)} · {safe(s.Certificate)}
                      </div>
                    </div>
                    <div style={{ fontWeight: 700, fontFamily: "monospace" }}>${Number(s.Price || 0).toLocaleString()}</div>
                    <Button size="small" danger onClick={() => handleRemoveStone(s.DInventoryID)}>
                      {t("Remove")}
                    </Button>
                  </div>
                ))}
              </div>

              <div>
                <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 8, padding: 16, marginBottom: 16 }}>
                  <h3 style={{ marginTop: 0 }}>{isMemo ? t("Memo request") : t("Purchase order")}</h3>
                  {stones.map((s) => (
                    <div key={s.DInventoryID} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "4px 0" }}>
                      <span>
                        {safe(s.Carat)}ct {safe(s.Shape)} {safe(s.Color)}/{safe(s.Clarity)}
                      </span>
                      <span>${Number(s.Price || 0).toLocaleString()}</span>
                    </div>
                  ))}
                  <div style={{ borderTop: "1px solid #eee", marginTop: 10, paddingTop: 10 }}>
                    <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".5px", color: "#6b7689", fontWeight: 600 }}>
                      {isMemo ? t("Value at list · before memo terms") : t("Order value")}
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700, fontFamily: "monospace" }}>${total.toLocaleString()}</div>
                    <div style={{ fontSize: 12, color: "#6b7689", marginTop: 3 }}>
                      {isMemo ? t("Supplier may add a memo margin and shipping when it ships") : t("Shipping is confirmed by the supplier when it ships")}
                    </div>
                  </div>
                </div>

                <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 8, padding: 16, marginBottom: 16 }}>
                  <h3 style={{ marginTop: 0 }}>{t("Addresses")}</h3>
                  <div style={{ marginBottom: 10 }}>
                    <label style={{ display: "block", fontSize: 11, textTransform: "uppercase", letterSpacing: ".5px", color: "#6b7689", fontWeight: 600, marginBottom: 5 }}>
                      {t("Deliver to · your receiving address")}
                    </label>
                    {receivingAddresses.length ? (
                      <Select style={{ width: "100%" }} value={selectedAddressId} onChange={setSelectedAddressId}>
                        {receivingAddresses.map((a) => (
                          <Option key={a.LocationId} value={a.LocationId}>
                            {safe(a.LocationName)} — {safe(a.City)}
                          </Option>
                        ))}
                      </Select>
                    ) : (
                      <div style={{ background: "#fff7f6", border: "1px dashed #e2574c", borderRadius: 8, padding: "10px 12px", color: "#e2574c" }}>
                        <b>{t("None on file")}</b>
                      </div>
                    )}
                  </div>
                  <RouteCard
                    fromAddress={
                      supplierShippingAddress
                        ? {
                            Label: safe(supplierShippingAddress.LocationName),
                            ContactName: safe(supplierShippingAddress.ContactName),
                            Address: safe(supplierShippingAddress.Address),
                            City: safe(supplierShippingAddress.City),
                            State: safe(supplierShippingAddress.State),
                            Zip: safe(supplierShippingAddress.Zip),
                            Country: safe(supplierShippingAddress.Country),
                            Phone: safe(supplierShippingAddress.Phone),
                          }
                        : null
                    }
                    toAddress={
                      selectedAddress
                        ? {
                            Label: safe(selectedAddress.LocationName),
                            ContactName: safe(selectedAddress.ContactName),
                            Address: safe(selectedAddress.Address),
                            City: safe(selectedAddress.City),
                            State: safe(selectedAddress.State),
                            Zip: safe(selectedAddress.Zip),
                            Country: safe(selectedAddress.Country),
                            Phone: safe(selectedAddress.Phone),
                          }
                        : null
                    }
                    fromPartyName={supplierName}
                    toPartyName={dealerCompany}
                  />
                </div>

                <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 8, padding: 16 }}>
                  <h3 style={{ marginTop: 0 }}>{t("Terms")}</h3>
                  {isMemo ? (
                    <div style={{ marginBottom: 10 }}>
                      <label style={{ display: "block", fontSize: 11, textTransform: "uppercase", letterSpacing: ".5px", color: "#6b7689", fontWeight: 600, marginBottom: 5 }}>
                        {t("Memo period")}
                      </label>
                      <Select style={{ width: "100%" }} value={memoDays} onChange={setMemoDays}>
                        <Option value="7">{t("7 days")}</Option>
                        <Option value="14">{t("14 days")}</Option>
                        <Option value="21">{t("21 days")}</Option>
                        <Option value="30">{t("30 days")}</Option>
                      </Select>
                    </div>
                  ) : (
                    <div style={{ marginBottom: 10 }}>
                      <label style={{ display: "block", fontSize: 11, textTransform: "uppercase", letterSpacing: ".5px", color: "#6b7689", fontWeight: 600, marginBottom: 5 }}>
                        {t("Payment terms")}
                      </label>
                      <Select style={{ width: "100%" }} value={terms} onChange={setTerms}>
                        <Option value="NET30">{t("Net 30")}</Option>
                        <Option value="NET60">{t("Net 60")}</Option>
                        <Option value="WIRE">{t("Wire before shipping")}</Option>
                      </Select>
                    </div>
                  )}
                  <div style={{ marginBottom: 10 }}>
                    <label style={{ display: "block", fontSize: 11, textTransform: "uppercase", letterSpacing: ".5px", color: "#6b7689", fontWeight: 600, marginBottom: 5 }}>
                      {t("Your reference")}
                    </label>
                    <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder={t("Customer name or ticket")} />
                  </div>
                  <Button type="primary" block disabled={blocked} loading={sending} onClick={handleSend}>
                    {isMemo ? t("Send memo request") : t("Place purchase order")} — ${total.toLocaleString()}
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </Spin>
    </div>
  );
};

export default Basket;
